import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ASSET_ROUTE_URL } from '../../../constants/routes';
import { cn } from '../../../lib/classNameHelpers';
import { formatPercentage } from '../../../lib/formatters';
import { Button } from '../../catalyst-ui-kit/button';
import { ColoredMetric } from '../../ColoredMetric/ColoredMetric';
import { ProductWithPrice } from './TradeFavorites';

const SCROLL_THRESHOLD = 1;
const SCROLL_PERCENTAGE = 0.8;

type ProductDisplayProps = {
  item: ProductWithPrice;
  onSelectProduct: (ticker: string) => void;
};

function ProductDisplay({ item, onSelectProduct }: ProductDisplayProps) {
  const { ticker, displayTicker, change24hrPercent } = item;

  return (
    <Button
      text
      size="2xs"
      role="link"
      color="tertiary"
      onClick={() => {
        onSelectProduct(ticker);
      }}
      className="group flex items-center gap-1 whitespace-nowrap"
    >
      {displayTicker}
      <ColoredMetric className="text-3xs" value={change24hrPercent} formatFn={formatPercentage} />
    </Button>
  );
}

type ScrollableSectionProps = {
  className?: string;
  label: string;
  data: ProductWithPrice[];
};

export function ScrollableSection({ label, data, className }: ScrollableSectionProps) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate({ from: ASSET_ROUTE_URL });

  const handleSelectProduct = useCallback(
    (ticker: string) => {
      void navigate({
        to: ASSET_ROUTE_URL,
        params: { asset: ticker },
        search: (prev) => prev,
      });
    },
    [navigate]
  );

  useEffect(() => {
    const checkScrollability = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - SCROLL_THRESHOLD);
      }
    };

    const scrollContainer = scrollRef.current;

    if (scrollContainer) {
      checkScrollability();
      scrollContainer.addEventListener('scroll', checkScrollability);
    }

    return () => {
      scrollContainer?.removeEventListener('scroll', checkScrollability);
    };
    // data.length is here to allow the scrollability to be recalculated when the data changes
  }, [data.length, scrollRef]);

  const scrollLeft = () => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * SCROLL_PERCENTAGE;
      scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * SCROLL_PERCENTAGE;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className={cn('flex min-w-0 items-center', className)}>
      <p className="text-2xs leading-none text-contentTertiary">{label}</p>
      <Button
        className={cn('mx-1', {
          'invisible pointer-events-none': !canScrollLeft,
        })}
        text
        size="sm"
        color="secondary"
        onClick={scrollLeft}
        aria-label="Scroll left"
      >
        <ChevronLeftIcon className="size-4" />
      </Button>
      <div
        ref={scrollRef}
        role="region"
        aria-label="Scrollable product list"
        className="scrollbar-hide flex shrink gap-3 overflow-x-auto scroll-smooth py-1"
        style={
          {
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '--fade-left': canScrollLeft ? 'transparent' : 'black',
            '--fade-right': canScrollRight ? 'transparent' : 'black',
            maskImage:
              'linear-gradient(to right, var(--fade-left) 0%, black 40px, black calc(100% - 40px), var(--fade-right) 100%)',
          } as React.CSSProperties
        }
      >
        {data.map((item) => (
          <ProductDisplay
            key={item.displayTicker}
            item={item}
            onSelectProduct={handleSelectProduct}
          />
        ))}
      </div>
      <Button
        text
        size="sm"
        color="secondary"
        onClick={scrollRight}
        aria-label="Scroll right"
        className={cn({
          'invisible pointer-events-none': !canScrollRight,
        })}
      >
        <ChevronRightIcon className="size-4" />
      </Button>
    </div>
  );
}
